import { Box, Flex, useMantineTheme } from '@mantine/core';
import type { ComputeCostTimeSeries } from '@repo/api-codegen';
import { Text } from '@repo/foundations';
import { unparse } from 'papaparse';
import { memo, useCallback, useMemo } from 'react';
import { BaseOverviewChart } from '../../../../components/Overview/BaseOverviewChart';
import { saveBlob } from '../../../../lib/models/misc';
import { dateFilterOptions } from '../../constants';
import type { TimeRangeValue } from '../../types';
import { getGroupKeys, processTimeseriesData } from './utils';

export const ComputeCostChart = memo(
	({
		timeseries,
		dateFilter,
		setDateFilter,
		setTimeRangeValue,
		timeRangeValue,
		dateRangeOption,
		setDateRangeOption,
		displayAs,
	}: {
		timeseries?: ComputeCostTimeSeries;
		dateFilter: string;
		setDateFilter: (dateFilter: string) => void;
		setTimeRangeValue: (timeRangeValue: TimeRangeValue) => void;
		timeRangeValue: TimeRangeValue;
		dateRangeOption: string;
		setDateRangeOption: (dateRangeOption: string) => void;
		displayAs: 'cost' | 'duration';
	}) => {
		const theme = useMantineTheme();

		const chartData = useMemo(
			() => processTimeseriesData(timeseries?.data),
			[timeseries]
		);

		// Get all unique group keys from the data
		const groupKeys = useMemo(() => getGroupKeys(chartData), [chartData]);

		// Create channels for each group key or use default total values
		const channels = useMemo(() => {
			const colorPalette = [
				theme.other.getColor('icon/primary/default'),
				theme.other.getColor('icon/emphasis/default'),
				theme.other.getColor('icon/success/default'),
				theme.other.getColor('icon/warning/default'),
				theme.other.getColor('icon/caution/default'),
				theme.other.getColor('icon/critical/default'),
			];

			// If no group keys, use default total values
			if (groupKeys.length === 0) {
				if (displayAs === 'cost') {
					return [
						{
							label: 'Total Cost',
							x: (d: Record<string, unknown>) =>
								new Date(d.timestamp as string),
							y: (d: Record<string, unknown>) => (d.total_cost as number) ?? 0,
							renderArea: true,
							areaOpacity: 0.3,
							color: colorPalette[0],
							zIndex: 10,
						},
						{
							label: 'vCPU Cost',
							x: (d: Record<string, unknown>) =>
								new Date(d.timestamp as string),
							y: (d: Record<string, unknown>) => (d.vcpu_cost as number) ?? 0,
							renderArea: true,
							areaOpacity: 0.3,
							color: colorPalette[1],
							zIndex: 9,
						},
						{
							label: 'Memory Cost',
							x: (d: Record<string, unknown>) =>
								new Date(d.timestamp as string),
							y: (d: Record<string, unknown>) => (d.memory_cost as number) ?? 0,
							renderArea: true,
							areaOpacity: 0.3,
							color: colorPalette[2],
							zIndex: 8,
						},
					];
				} else {
					return [
						{
							label: 'Avg Duration (seconds)',
							x: (d: Record<string, unknown>) =>
								new Date(d.timestamp as string),
							y: (d: Record<string, unknown>) =>
								(d.avg_duration_seconds as number) ?? 0,
							renderArea: true,
							areaOpacity: 0.3,
							color: colorPalette[3],
							zIndex: 10,
						},
					];
				}
			}

			// Otherwise, create channels for each group key
			return groupKeys.map((groupKey, index) => {
				const label = timeseries?.group_by_labels?.[groupKey] || groupKey;
				return {
					label,
					x: (d: Record<string, unknown>) => new Date(d.timestamp as string),
					y: (d: Record<string, unknown>) => {
						const groupData = (
							d.group_data as
								| Record<
										string,
										{
											total_cost?: number;
											avg_duration_seconds?: number;
										}
								  >
								| undefined
						)?.[groupKey];
						if (displayAs === 'cost') {
							return groupData?.total_cost ?? 0;
						}
						return groupData?.avg_duration_seconds ?? 0;
					},
					renderArea: true,
					areaOpacity: 0.3,
					color: colorPalette[index % colorPalette.length],
					zIndex: index + 10,
				};
			});
		}, [groupKeys, theme, displayAs, timeseries?.group_by_labels]);

		const tooltipDataRenderer = useCallback(
			(dataIndex: number) => {
				const point = chartData[dataIndex];
				if (!point) return null;

				const isRange =
					dateRangeOption === 'week' || dateRangeOption === 'month';
				const nextPoint = chartData[dataIndex + 1];
				const showRange = isRange && nextPoint;

				return (
					<Box p="sm">
						<Text mb="sm" size="sm" color="text/secondary/default">
							{showRange ? (
								<>
									{point.timestamp.toLocaleDateString([], {
										month: 'long',
										day: 'numeric',
									})}
									{' - '}
									{nextPoint.timestamp.toLocaleDateString([], {
										month: 'long',
										day: 'numeric',
										year: 'numeric',
									})}
								</>
							) : (
								point.timestamp.toLocaleDateString([], {
									month: 'long',
									day: 'numeric',
									year: 'numeric',
								})
							)}
						</Text>
						<Flex direction="column" gap="xs" mt="sm">
							{groupKeys.length === 0 ? (
								// Show default values when no groups
								<>
									<Flex justify="space-between" gap="sm">
										<Text size="sm">Total requests</Text>
										<Text fw={500} size="sm">
											{point.total_requests.toLocaleString()}
										</Text>
									</Flex>
									{displayAs === 'cost' ? (
										<>
											<Flex justify="space-between" gap="sm">
												<Text size="sm">Total cost</Text>
												<Text fw={500} size="sm">
													${point.total_cost.toFixed(4)}
												</Text>
											</Flex>
											<Flex justify="space-between" gap="sm">
												<Text size="sm">vCPU cost</Text>
												<Text fw={500} size="sm">
													${point.vcpu_cost.toFixed(4)}
												</Text>
											</Flex>
											<Flex justify="space-between" gap="sm">
												<Text size="sm">Memory cost</Text>
												<Text fw={500} size="sm">
													${point.memory_cost.toFixed(4)}
												</Text>
											</Flex>
										</>
									) : (
										<Flex justify="space-between" gap="sm">
											<Text size="sm">Avg duration</Text>
											<Text fw={500} size="sm">
												{point.avg_duration_seconds.toFixed(2)}s
											</Text>
										</Flex>
									)}
									<Flex justify="space-between" gap="sm">
										<Text size="sm">Success rate</Text>
										<Text fw={500} size="sm">
											{point.success_rate.toFixed(1)}%
										</Text>
									</Flex>
								</>
							) : (
								// Show group data when group keys exist
								groupKeys.map((groupKey, index) => {
									const groupData = point.group_data?.[groupKey];
									if (!groupData) return null;

									const label =
										timeseries?.group_by_labels?.[groupKey] || groupKey;

									return (
										<Flex key={groupKey} justify="space-between" gap="sm">
											<Flex gap="xs" align="center">
												<Box
													w={16}
													h={2}
													bg={channels[index]?.color}
													style={{
														borderRadius: theme.radius.md,
													}}
													aria-hidden="true"
												/>
												<Text size="sm">{label}</Text>
											</Flex>
											<Text fw={500} size="sm">
												{displayAs === 'cost'
													? `$${groupData.total_cost.toFixed(4)}`
													: `${groupData.avg_duration_seconds.toFixed(2)}s`}
											</Text>
										</Flex>
									);
								})
							)}
							<Flex justify="space-between" gap="sm">
								<Text size="sm">Total Requests</Text>
								<Text fw={500} size="sm">
									{point.total_requests.toLocaleString()}
								</Text>
							</Flex>
						</Flex>
					</Box>
				);
			},
			[
				chartData,
				dateRangeOption,
				groupKeys,
				displayAs,
				timeseries?.group_by_labels,
				channels,
				theme,
			]
		);

		// CSV export function
		const handleExportCsv = useCallback(() => {
			if (!chartData.length) return;

			// Prepare CSV data
			const csvData = chartData.map((point) => {
				const baseData = {
					timestamp: point.timestamp.toISOString(),
					total_requests: point.total_requests ?? 0,
				};

				// Add group-specific data if available
				if (groupKeys.length === 0) {
					// No groups - use total values
					return {
						...baseData,
						total_cost: point.total_cost ?? 0,
						vcpu_cost: point.vcpu_cost ?? 0,
						memory_cost: point.memory_cost ?? 0,
						avg_duration_seconds: point.avg_duration_seconds ?? 0,
						success_rate: point.success_rate ?? 0,
					};
				} else {
					// Groups available - add each group's data
					const groupData: Record<string, number> = {};
					groupKeys.forEach((groupKey) => {
						const groupPoint = point.group_data?.[groupKey];
						const label = timeseries?.group_by_labels?.[groupKey] || groupKey;
						groupData[`${label}_total_requests`] =
							groupPoint?.total_requests ?? 0;
						groupData[`${label}_total_cost`] = groupPoint?.total_cost ?? 0;
						groupData[`${label}_vcpu_cost`] = groupPoint?.vcpu_cost ?? 0;
						groupData[`${label}_memory_cost`] = groupPoint?.memory_cost ?? 0;
						groupData[`${label}_avg_duration_seconds`] =
							groupPoint?.avg_duration_seconds ?? 0;
						groupData[`${label}_success_rate`] = groupPoint?.success_rate ?? 0;
					});
					return {
						...baseData,
						...groupData,
					};
				}
			});

			// Generate CSV
			const csv = unparse(csvData, {
				escapeFormulae: true,
			});

			// Create filename with date range
			const startDate = chartData[0]?.timestamp.toLocaleDateString([], {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
			});
			const endDate = chartData[
				chartData.length - 1
			]?.timestamp.toLocaleDateString([], {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
			});
			const filename = `compute_cost_${startDate}_to_${endDate}.csv`;

			// Download the file
			saveBlob(new Blob([csv], { type: 'text/csv' }), filename);
		}, [chartData, groupKeys, timeseries?.group_by_labels]);

		// Create a modified timeseries object that includes the required properties
		const modifiedTimeseries = useMemo(
			() => ({
				...timeseries,
				points: timeseries?.data ?? [],
				granularity: dateRangeOption,
				available_channels: [],
				available_dimensions: [],
			}),
			[timeseries, dateRangeOption]
		);

		return (
			<BaseOverviewChart
				title={
					displayAs === 'cost'
						? 'Compute Cost Over Time'
						: 'Job Duration Over Time'
				}
				channels={channels}
				timeseries={modifiedTimeseries}
				dateFilter={dateFilter}
				setDateFilter={setDateFilter}
				dateFilterOptions={dateFilterOptions}
				setTimeRangeValue={setTimeRangeValue}
				timeRangeValue={timeRangeValue}
				dateRangeOption={dateRangeOption}
				setDateRangeOption={setDateRangeOption}
				tooltipDataRenderer={tooltipDataRenderer}
				analyticsEventPrefix="compute_cost_chart"
				onExportCsv={handleExportCsv}
				showExportButton={true}
			/>
		);
	}
);

ComputeCostChart.displayName = 'ComputeCostChart';
